import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserData } from '../../../../hooks/useUserData';
import NotifyPermissionPrompt from '../../../../ui/notifyPermission/NotifyPermissionPrompt';
import useMapModalStore from '../../../../stores/useMapModalStore';
import ModalFrame from '../../../../ui/primitives/ModalFrame';
import CelebrationConfetti from '../../../../components/CelebrationConfetti';
import type { UseTutorialRewardClaimResult } from '../../useMapOnboarding';
import {
  ONBOARDING_REWARD_EXP_ICON_SRC,
  ONBOARDING_REWARD_PEARL_ICON_SRC,
  OnboardingRewardAmountRow,
} from './onboardingRewardShared';

interface TutorialRewardHostProps {
  /** MapCanvas-owned claim flow state (useTutorialRewardClaim). */
  reward: UseTutorialRewardClaimResult;
}

/** Tutorial completion reward modal (`activeModal === 'tutorialReward'`). */
export default function TutorialRewardHost({ reward }: TutorialRewardHostProps) {
  const { rewardPhase, setRewardPhase, totalPearlsAwarded, totalExpAwarded, notifyBonus } = reward;
  const { t, i18n } = useTranslation();
  const onboardingRewardNumberLocale = i18n.language?.startsWith('uk') ? 'uk-UA' : 'en-US';
  const isTutorialRewardModalOpen = useMapModalStore((s) => s.activeModal === 'tutorialReward');
  const openModal = useMapModalStore((s) => s.openModal);
  const closeModal = useMapModalStore((s) => s.closeModal);
  const openOnboardingPlotFreeAfterReward = useMapModalStore((s) => s.payload.openPlotFreeAfterReward);
  const setOpenOnboardingPlotFreeAfterReward = useMapModalStore((s) => s.setOpenPlotFreeAfterReward);

  // The tutorial's last ask: let the bot message you, for a doubled reward.
  // Once shown it stays until the player is done with it (granting flips the
  // user flag mid-flow).
  const { data: userData } = useUserData();
  const [notifyShown, setNotifyShown] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);
  const [grantedBonus, setBonus] = useState({ pearls: 0, exp: 0 });
  // Messages were already allowed: the claim paid the doubling, there is nothing to ask.
  const alreadyAllowed = notifyBonus.pearls > 0;
  const bonus = alreadyAllowed ? notifyBonus : grantedBonus;
  const askNotify = rewardPhase === 'done' && !notifyDone && !alreadyAllowed
    && (notifyShown || !userData?.notifications_warmup_granted);
  if (askNotify && !notifyShown) setNotifyShown(true);

  const finishRewardFlow = () => {
    // Still claiming: the reward must land before the player moves on.
    if (rewardPhase !== 'done') return;
    setNotifyDone(true);
    setRewardPhase('claim');
    if (openOnboardingPlotFreeAfterReward) {
      setOpenOnboardingPlotFreeAfterReward(false);
      openModal('plotFree');
    } else {
      closeModal();
    }
  };

  return (
        <ModalFrame
          isOpen={isTutorialRewardModalOpen}
          onClose={finishRewardFlow}
          closeOnBackdropClick={false}
          variant="dialog"
          className="max-w-[400px] rounded-xl border-[3px] border-deep-blue"
          renderHeader={() => (
            <div
              style={{
                padding: '20px 20px 16px 20px',
                textAlign: 'center',
                fontFamily: 'Rubik One, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                color: '#fff',
                lineHeight: '1.3',
              }}
            >
              {t("Tutorial completed!")}
            </div>
          )}
        >
          {/* Phase: claiming (auto) — loading spinner */}
          {rewardPhase === 'claim' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '24px 0' }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                border: '3px solid rgba(55,191,164,0.3)', borderTopColor: '#37BFA4',
                animation: 'spin 0.8s linear infinite',
              }} />
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: 15, fontWeight: 300, color: 'rgba(255,255,255,0.7)' }}>
                {t("Claiming...")}
              </div>
            </div>
          )}

          {/* Phase: done — the reward that was credited */}
          {rewardPhase === 'done' && (
            <>
              {/* Again when the reward doubles. */}
              <CelebrationConfetti key={bonus.pearls} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: 15, fontWeight: 300, lineHeight: '1.4', color: '#fff' }}>
                  {grantedBonus.pearls > 0 ? t('New reward:') : t('Congratulations tutorial reward headline')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', width: '100%' }}>
                  <OnboardingRewardAmountRow iconSrc={ONBOARDING_REWARD_PEARL_ICON_SRC} amount={totalPearlsAwarded + bonus.pearls} numberLocale={onboardingRewardNumberLocale} />
                  <OnboardingRewardAmountRow iconSrc={ONBOARDING_REWARD_EXP_ICON_SRC} amount={totalExpAwarded + bonus.exp} numberLocale={onboardingRewardNumberLocale} />
                </div>
                {alreadyAllowed && (
                  <div style={{ fontFamily: 'Lato, sans-serif', fontSize: 14, lineHeight: '1.4', color: 'var(--color-gold)' }}>
                    🔔 {t("Thank you for letting us message you in Telegram — your reward is doubled!")}
                  </div>
                )}
              </div>
              {askNotify ? (
                <NotifyPermissionPrompt
                  source="onboarding"
                  rewardPearls={totalPearlsAwarded}
                  doubles
                  onGranted={setBonus}
                  // Allowed: stay to see the doubled reward, then Continue.
                  onDone={(granted) => (granted ? setNotifyDone(true) : finishRewardFlow())}
                />
              ) : (
              <button
                type="button"
                onClick={finishRewardFlow}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: '#37BFA4',
                  border: 'none',
                  borderRadius: 100,
                  color: '#fff',
                  fontFamily: 'Rubik One, sans-serif',
                  fontSize: 16,
                  fontWeight: 400,
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                {t("Continue")}
              </button>
              )}
            </>
          )}
        </ModalFrame>
  );
}
